import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useIxfHoursV1OperatingHoursServiceGetOperatingHours } from '@interxfi/api-client/generated/hooks';
import { TenantContext, useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { DropdownMenuItem } from '@interxfi/design-system/components/dropdown-menu/list-item/dropdown-menu-list-item';
import { HoursIndicator as GenericHoursIndicator } from '@interxfi/design-system/components/hours-indicator/hours-indicator';
import { Typography } from '@interxfi/design-system/components/typography';
import DisclosureRightSmallIcon from '@interxfi/design-system/icons/react/icon-disclosure-right-small';
import { useCurrentDate } from '@interxfi/design-system/util/date';
import { type IxfHoursV1OperatingDay, type IxfHoursV1OperatingHours, IxfHoursV1Region } from '@interxfi/types';
import { getDateFormatter } from '@interxfi/utilities/date/formatting';
import { getOpenWindow, useIsScheduleOpen } from '../../../../features/hours/is-open';
import styles from './hours-indicator.module.scss';

function toClock(iso: string, day: string, timeZone: string) {
  const date = new Date(iso);
  const time = getDateFormatter('en-US', { hour: 'numeric', minute: '2-digit', timeZone }).format(date).replace(/\s/g, '').toLowerCase();
  const localDate = getDateFormatter('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone }).format(date);
  const weekday = getDateFormatter('en-US', { weekday: 'short', timeZone }).format(date);

  return { ms: date.valueOf(), label: localDate === day ? time : `${time} ${weekday}` } as const;
}

function getExceptionLabel(
  effective: IxfHoursV1OperatingHours,
  defaultHours: IxfHoursV1OperatingHours | undefined,
  day: string,
  timeZone: string,
): string {
  const actual = getOpenWindow(effective);

  if (!actual) {
    return `Closed ${day}`;
  }

  const open = toClock(actual.opensAt, day, timeZone);
  const close = toClock(actual.closesAt, day, timeZone);
  const summary = `${open.label}–${close.label}`;
  const fallback = `${day} hours: ${summary}`;

  // A default value is only present when an exception exists on a date. If closed, there's nothing to diff.
  const usual = getOpenWindow(defaultHours);
  if (!usual) {
    return fallback;
  }

  const usualOpen = toClock(usual.opensAt, day, timeZone);
  const usualClose = toClock(usual.closesAt, day, timeZone);
  const openDiff = open.ms !== usualOpen.ms;
  const closeDiff = close.ms !== usualClose.ms;

  if (openDiff && closeDiff) {
    return `Special hours on ${day}: ${summary}`;
  }

  if (openDiff) {
    return `${open.ms < usualOpen.ms ? 'Early' : 'Late'} open ${day} at ${open.label}`;
  }

  if (closeDiff) {
    return `${close.ms < usualClose.ms ? 'Early' : 'Late'} close ${day} at ${close.label}`;
  }

  return fallback;
}

function buildHoursSummary(
  closeMobileNav: VoidFunction,
  timeZone: string,
  days: ReadonlyArray<IxfHoursV1OperatingDay | undefined>,
): DropdownMenuItem[] {
  const base: DropdownMenuItem[] = [
    {
      id: 'manageLink',
      group: 'View & Manage Hours',
      LinkElement: Link,
      to: '/hours',
      onClick: closeMobileNav,
      label: (
        <HorizontalStack align="start" blockAlign="center" gap={2} wrap="nowrap">
          <Typography lineHeight="1rem" size="0.875rem">
            View &amp; Manage Hours
          </Typography>

          <DisclosureRightSmallIcon aria-hidden width={16} />
        </HorizontalStack>
      ),
    },
  ];

  const exceptionLabels = days.reduce((acc: { date: string; label: string }[], day) => {
    if (day?.exception) {
      const core = day.trading?.core;

      if (core) {
        const label = getExceptionLabel(core.effective, core.default, day.date, timeZone);
        acc.push({ date: day.date, label });
      }
    }

    return acc;
  }, []);

  if (exceptionLabels.length > 0) {
    base.unshift({
      id: 'details',
      group: 'Exception',
      label: (
        <VerticalStack className={styles.exceptionSummary} gap={2}>
          {exceptionLabels.map(({ date, label }) => (
            <Typography key={date} lineHeight="1rem" size="0.75rem" wrap="break-word">
              {label}
            </Typography>
          ))}
        </VerticalStack>
      ),
    });
  }

  return base;
}

interface HoursIndicatorProps {
  closeMobileNav: VoidFunction;
  isMobile: boolean;
  timeZone: string;
}

export function HoursIndicator({ closeMobileNav, isMobile, timeZone }: HoursIndicatorProps) {
  const { activeTenantContext } = useActiveRealmAPIContext();
  const { dateAsString: currentDate } = useCurrentDate(timeZone);
  const shouldShowMarketHours = !activeTenantContext || [TenantContext.Clearing, TenantContext.Program].includes(activeTenantContext);

  // Same query key as useIsScheduleOpen below, so React Query dedupes the request.
  const { data } = useIxfHoursV1OperatingHoursServiceGetOperatingHours(
    !shouldShowMarketHours ? undefined : { date: currentDate, timezone: timeZone, key: IxfHoursV1Region.Us },
    {
      refetchOnWindowFocus: false,
      refetchInterval: false,
    },
  );
  const marketStatus = useIsScheduleOpen(shouldShowMarketHours, IxfHoursV1Region.Us, 'trading', 'core', timeZone);

  const today = data?.today;
  const tomorrow = data?.tomorrow;
  const hoursItems = useMemo(() => buildHoursSummary(closeMobileNav, timeZone, [today, tomorrow]), [closeMobileNav, timeZone, today, tomorrow]);

  if (!shouldShowMarketHours) {
    return null;
  }

  return (
    <GenericHoursIndicator
      align={isMobile ? 'start' : 'end'}
      closedLabel="Markets Closed"
      items={hoursItems}
      openLabel="Markets Open"
      state={marketStatus}
    />
  );
}
